import { h as __h } from "preact";
/** @import { Signal } from "@preact/signals"; */
import { Component, h } from "preact";
/** @import { ComponentChildren, RefObject } from "preact"; */

import { identity } from "~/lib/identity.js";
import html from "~/util/html.js";
/** @import { paginatedResponse, query } from "~/lib/api.js"; */

/** @import { DropdownItem } from "~/component/Dropdown.js"; */
import Data from "~/component/Data.js";
import ChipList from "~/admin/component/ChipList.js";
/** @import Pagination from "~/admin/component/Pagination.js"; */
/** @import { TableHeader } from "~/admin/component/Table.js"; */
import Table from "~/admin/component/Table.js";
import UserStack from "~/admin/component/UserStack.js";
import ComputerChip from "../computer/ComputerChip.js";
import GroupChip from "../group/GroupChip.js";
import { changeRole } from "./actions/changeRole.js";
import { deleteMembers } from "./actions/deleteMembers.js";
import { manageGroups } from "./actions/manageGroups.js";
import Roles from "~/admin/lib/roles.js";
/** @import { adminMemberResponse } from "~/backend/validate.js"; */

/**
 * @extends {Component<MemberTableProps>}
 *
 * @typedef {object} MemberTableProps
 * @property {string} orgId
 * @property {ReturnType<
 * 	typeof query<
 * 		`/ui/admin/orgs/${string}/members`,
 * 		ReturnType<typeof paginatedResponse<adminMemberResponse>>
 * 	>
 * >} query
 * @property {Signal<Map<string, adminMemberResponse>>} [checked]
 * @property {ComponentChildren} [footer]
 * @property {(h: TableHeader[]) => TableHeader[]} [headers]
 * @property {(
 * 	items: (DropdownItem & { key?: string })[],
 * 	member: adminMemberResponse,
 * 	anchor: RefObject<HTMLButtonElement>,
 * ) => DropdownItem[]} [onTripleDots]
 * @property {InstanceType<typeof Pagination.State>} [paginate]
 * @property {InstanceType<typeof Table.Sort>} [sort]
 * @property {false} [scroll]
 * @property {boolean} [mappingsLock] Disables membership edits while SCIM mappings are on
 */
export default class MemberTable extends Component {
  /** @param {{ local?: boolean }} [options] */
  static sort({
    local = false
  } = {}) {
    return new Table.Sort({
      keys: ["created_at", "name", "email", "role"],
      by: "name",
      local
    });
  }
  get #headers() {
    const h = [{
      id: "user",
      label: "Person",
      sort: "name"
    }, {
      id: "role",
      label: "Role",
      sort: "role"
    }, {
      id: "groups",
      label: "Groups"
    }, {
      id: "computers",
      label: "Computers"
    }];
    if (this.props.headers) return this.props.headers(h);
    return h;
  }

  /**
   * @returns {(
   * 	member: adminMemberResponse,
   * 	anchor: { current: HTMLButtonElement },
   * ) => DropdownItem[]}
   */
  get #onTripleDots() {
    const canEditMembers = identity.permission(this.props.orgId, "members", "edit");
    const canEditGroups = identity.permission(this.props.orgId, "groups", "edit");
    return (member, anchor) => {
      const tooltip = this.props.mappingsLock && "Managed by directory mappings";
      /** @type {(DropdownItem & { key?: string })[]} */
      const items = [];
      if (canEditMembers) {
        items.push({
          icon: "pencil",
          text: "Change role",
          disabled: this.props.mappingsLock,
          tooltip,
          onSelect: async () => await changeRole({
            anchor: anchor.current,
            placement: "bottom-end",
            orgId: this.props.orgId
          }, member)
        });
      }
      if (canEditGroups) {
        items.push({
          icon: "users",
          text: "Manage groups",
          disabled: this.props.mappingsLock,
          tooltip,
          onSelect: async () => await manageGroups({
            anchor: anchor.current,
            placement: "bottom-end",
            orgId: this.props.orgId
          }, member)
        });
      }
      if (canEditMembers) {
        items.push({
          key: "remove",
          icon: "trash",
          text: "Remove",
          kind: "negative",
          disabled: this.props.mappingsLock,
          tooltip,
          onSelect: async () => {
            const res = await deleteMembers({
              orgId: this.props.orgId
            }, member.id);
            if (res) {
              if (this.props.checked) {
                const checked = new Map(this.props.checked.value);
                checked.delete(member.id);
                this.props.checked.value = checked;
              }
            }
          }
        });
      }
      if (this.props.onTripleDots) return this.props.onTripleDots(items, member, anchor);
      return items;
    };
  }
  render() {
    const checked = this.props.checked;
    return h(Data, {
      loading: this.props.query.loading && !this.props.query.data,
      error: this.props.query.error,
      children: h(/** @type {typeof Table<adminMemberResponse>} */Table, {
        label: "member",
        checked: checked?.value,
        onCheck: checked && (check => checked.value = check),
        headers: this.#headers,
        empty: "No members found.",
        footer: this.props.footer,
        rows: this.props.query.data?.data.map(member => {
          return {
            key: member.id,
            label: member.name,
            value: member,
            columns: {
              user: MemberTable.user(member),
              role: MemberTable.role(member),
              groups: MemberTable.groups(member, this.props.orgId),
              computers: MemberTable.computers(member, this.props.orgId)
            }
          };
        }) ?? [],
        paginate: this.props.paginate?.props(this.props.query),
        sort: this.props.sort?.props(),
        href: member => `/org/${this.props.orgId}/members/${member.id}`,
        onTripleDots: this.#onTripleDots,
        scroll: this.props.scroll
      })
    });
  }

  /** @param {adminMemberResponse} member */
  static user(member) {
    return __h(UserStack, {
      name: member.name,
      email: member.email
    });
  }

  /** @param {adminMemberResponse} member */
  static role(member) {
    return Roles.label("org", member.role);
  }

  /**
   * @param {adminMemberResponse} member
   * @param {string} org_id
   */
  static groups(member, org_id) {
    return __h(ChipList, {}, member.groups.map(group => __h(GroupChip, {
      key: group.id,
      group: {
        ...group,
        org_id
      }
    })));
  }

  /**
   * @param {adminMemberResponse} member
   * @param {string} org_id
   */
  static computers(member, org_id) {
    return __h(ChipList, {}, member.machines.map(host => __h(ComputerChip, {
      key: host.id,
      machine: {
        ...host,
        org_id
      }
    })));
  }
}